// TUT-450 — sessão, perfil e acesso de quem está logado: entrar e sair, tema, sessões ativas, notificações pessoais, senha e convites.
import { demoApi } from "../demo-data";
import type {
  AcceptInvitationInput,
  AcceptInvitationResult,
  CanalPessoal,
  MinhaPreferenciaDeNotificacao,
  MeuAcesso,
  MinhaSessao,
  LoginResult,
  InvitationContext,
  PasswordResetContextResult,
  Session,
  ThemePreference,
  MenuPreference,
  PreviaDaTrocaDeEmail,
} from "../types";
import { captureSession, esquecerSessao, request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDaSessao = {
  async session(): Promise<Session> {
    return captureSession(
      useDemoApi
        ? await demoApi.session()
        : await request<Session>("/auth/session"),
    );
  },
  async login(input: {
    email: string;
    password: string;
    tenantSlug?: string;
  }): Promise<LoginResult> {
    const result: LoginResult = useDemoApi
      ? await demoApi.login(input.email)
      : await request<LoginResult>("/auth/login", {
          method: "POST",
          body: JSON.stringify(input),
        });
    return "requiresTenantSelection" in result
      ? result
      : captureSession(result);
  },
  async logout(): Promise<void> {
    try {
      if (useDemoApi) await demoApi.logout();
      else await request<void>("/auth/logout", { method: "POST" });
    } finally {
      // Mesmo que o servidor recuse (sessão já caída), o token desta aba é
      // de uma sessão que acabou.
      esquecerSessao();
    }
  },
  /**
   * TUT-153 — grava a preferência de tema no perfil. A tela já trocou de cor
   * via adoptThemePreference(); esta chamada só torna a escolha durável, então
   * falha de rede não pode desfazer o que o olho já viu.
   */
  async setThemePreference(theme: ThemePreference): Promise<void> {
    if (useDemoApi) return;
    await request<{ ok: true }>("/auth/theme", {
      method: "POST",
      body: JSON.stringify({ theme }),
    });
  },
  /**
   * TUT-465 — grava o jeito do menu lateral no perfil. A tela já mudou via
   * `adoptMenuPreference()`; esta chamada só torna a escolha durável.
   */
  async setMenuPreference(menu: MenuPreference): Promise<void> {
    if (useDemoApi) return;
    await request<{ ok: true }>("/auth/menu", {
      method: "POST",
      body: JSON.stringify({ menu }),
    });
  },
  /** TUT-288b — os meus grupos e as minhas permissões, com a origem de cada uma. */
  meuAcesso(): Promise<MeuAcesso> {
    if (useDemoApi) return demoApi.meuAcesso();
    return request<MeuAcesso>("/auth/access");
  },
  /** TUT-288c — "Sessões ativas": as minhas, neste escritório. */
  minhasSessoes(): Promise<{ items: MinhaSessao[] }> {
    if (useDemoApi) return demoApi.minhasSessoes();
    return request<{ items: MinhaSessao[] }>("/auth/sessions");
  },
  encerrarSessao(id: string): Promise<{ ok: true }> {
    if (useDemoApi) return demoApi.encerrarSessao(id);
    return request<{ ok: true }>(`/auth/sessions/${encodeURIComponent(id)}/revoke`, {
      method: "POST",
    });
  },
  encerrarOutrasSessoes(): Promise<{ revoked: number }> {
    if (useDemoApi) return demoApi.encerrarOutrasSessoes();
    return request<{ revoked: number }>("/auth/sessions/revoke-others", {
      method: "POST",
    });
  },
  /** TUT-288d — "Minhas notificações": o que eu recebo, por evento e canal. */
  minhasNotificacoes(): Promise<{ items: MinhaPreferenciaDeNotificacao[] }> {
    if (useDemoApi) return demoApi.minhasNotificacoes();
    return request<{ items: MinhaPreferenciaDeNotificacao[] }>("/notifications/preferences");
  },
  mudarMinhaNotificacao(input: {
    eventKey: string;
    channel: CanalPessoal;
    receive: boolean;
  }): Promise<{ items: MinhaPreferenciaDeNotificacao[] }> {
    if (useDemoApi) return demoApi.mudarMinhaNotificacao(input);
    return request<{ items: MinhaPreferenciaDeNotificacao[] }>("/notifications/preferences", {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
  /** TUT-485 (D12) — a pessoa muda o PRÓPRIO nome, sem restrição. */
  renameOwnProfile(name: string): Promise<{ name: string }> {
    return useDemoApi
      ? demoApi.renameOwnProfile(name)
      : request<{ name: string }>("/auth/profile/name", {
          method: "POST",
          body: JSON.stringify({ name }),
        });
  },
  /** TUT-485 — pede a troca do próprio e-mail: o link vai ao endereço NOVO. */
  requestOwnEmailChange(input: {
    newEmail: string;
    currentPassword: string;
  }): Promise<{ message: string }> {
    return useDemoApi
      ? demoApi.requestOwnEmailChange(input)
      : request<{ message: string }>("/auth/profile/email", {
          method: "POST",
          body: JSON.stringify(input),
        });
  },
  /**
   * TUT-485 — a leitura prévia do link do e-mail: para onde a conta vai e quem
   * pediu. SÓ LÊ; quem muda o e-mail é `confirmEmailChange`.
   */
  previewEmailChange(token: string): Promise<PreviaDaTrocaDeEmail> {
    return useDemoApi
      ? demoApi.previewEmailChange(token)
      : request<PreviaDaTrocaDeEmail>(
          `/auth/profile/email/confirm?token=${encodeURIComponent(token)}`,
        );
  },
  /**
   * TUT-485 — confirma a troca para a pessoa desta sessão. É POST (com CSRF) e o
   * token vai no corpo, fora da URL da API.
   */
  confirmEmailChange(token: string): Promise<{ email: string }> {
    return useDemoApi
      ? demoApi.confirmEmailChange(token)
      : request<{ email: string }>("/auth/profile/email/confirm", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
  },
  changePassword(input: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ ok: true }> {
    return useDemoApi
      ? demoApi.changePassword()
      : request<{ ok: true }>("/auth/change-password", {
          method: "POST",
          body: JSON.stringify(input),
        });
  },
  requestPasswordReset(email: string): Promise<{ message: string }> {
    return useDemoApi
      ? demoApi.requestPasswordReset(email)
      : request<{ message: string }>("/password-resets", {
          method: "POST",
          body: JSON.stringify({ email }),
        });
  },
  passwordResetContext(token: string): Promise<PasswordResetContextResult> {
    return useDemoApi
      ? demoApi.passwordResetContext(token)
      : request<PasswordResetContextResult>("/password-resets/context", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
  },
  acceptPasswordReset(input: {
    token: string;
    password: string;
  }): Promise<{ email: string }> {
    return useDemoApi
      ? demoApi.acceptPasswordReset(input)
      : request<{ email: string }>("/password-resets/accept", {
          method: "POST",
          body: JSON.stringify(input),
        });
  },
  invitationContext(token: string): Promise<InvitationContext> {
    return useDemoApi
      ? demoApi.invitationContext(token)
      : request<InvitationContext>("/invitations/context", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
  },
  acceptInvitation(
    input: AcceptInvitationInput,
  ): Promise<AcceptInvitationResult> {
    return useDemoApi
      ? demoApi.acceptInvitation(input)
      : request<AcceptInvitationResult>("/invitations/accept", {
          method: "POST",
          body: JSON.stringify(input),
        });
  },
};
